import type { CSSProperties } from 'react'
import type { Role } from '@lms/shared-types'
import { FadeIn } from '../primitives/FadeIn'
import { roleLabel } from '../../lib/role'

const ROLE_OPTIONS: Role[] = [
  'employee',
  'manager',
  'hod',
  'hr',
  'director',
  'exec_director',
]

const MANAGER_ROLES = new Set<Role>(['manager', 'hod'])

function hasManagerRole(roles: Role[]): boolean {
  return roles.some((r) => MANAGER_ROLES.has(r))
}

interface RoleMultiSelectProps {
  value: Role[]
  onChange: (roles: Role[]) => void
  disabled?: boolean
}

const fieldsetStyle: CSSProperties = {
  border: 'none',
  padding: 0,
  margin: 0,
}

const legendStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: 'var(--text-3)',
  marginBottom: 8,
  padding: 0,
}

/** Visually-hidden checkbox — kept for a11y + label association (screen
 *  readers and tests still see real checkboxes); the chip <label> is the
 *  visible control. */
const hiddenCheckboxStyle: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
}

const chipWrapStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
}

function chipStyle(selected: boolean, disabled: boolean): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: 40,
    padding: '0 14px',
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
    userSelect: 'none',
    transition:
      'background var(--motion-duration-fast) var(--motion-ease-out),' +
      ' border-color var(--motion-duration-fast) var(--motion-ease-out),' +
      ' color var(--motion-duration-fast) var(--motion-ease-out)',
    border: '1px solid',
    borderColor: selected ? 'transparent' : 'var(--border)',
    background: selected ? 'var(--steel-blue)' : 'var(--surface)',
    color: selected ? 'var(--surface)' : 'var(--text-2)',
    opacity: disabled && !selected ? 0.55 : 1,
  }
}

const noteStyle: CSSProperties = {
  marginTop: 4,
  padding: '8px 10px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  fontSize: 12,
  color: 'var(--text-2)',
  lineHeight: 1.5,
}

export function RoleMultiSelect({ value, onChange, disabled = false }: RoleMultiSelectProps): JSX.Element {
  const showHint = hasManagerRole(value)

  function handleChange(role: Role, checked: boolean): void {
    if (disabled) return

    if (!checked) {
      // Unchecking — enforce min-one: refuse to produce empty array
      if (value.length <= 1) return
      onChange(value.filter((r) => r !== role))
    } else {
      onChange([...value, role])
    }
  }

  return (
    <fieldset style={fieldsetStyle}>
      <legend style={legendStyle}>Roles</legend>
      <div style={chipWrapStyle}>
        {ROLE_OPTIONS.map((role) => {
          const isChecked = value.includes(role)
          const isLastRemaining = isChecked && value.length === 1
          const isInputDisabled = disabled || isLastRemaining

          return (
            <label key={role} style={chipStyle(isChecked, isInputDisabled)}>
              <input
                type="checkbox"
                style={hiddenCheckboxStyle}
                checked={isChecked}
                disabled={isInputDisabled}
                onChange={(e) => handleChange(role, e.target.checked)}
              />
              {roleLabel(role)}
            </label>
          )
        })}
      </div>
      {showHint && (
        <FadeIn duration="fast" triggerKey="manager-hod-hint">
          <p role="note" style={noteStyle}>
            Granting Manager or HOD shows the manager surface. They only see a
            team once you assign one in Approval assignments.
          </p>
        </FadeIn>
      )}
    </fieldset>
  )
}
